/**
 * קפיצים ומחוות (6ו-ב, D31, A-186; סדר העבודה לעיצוב §3–§6, §9) — מודול פנימי, בלי תלות.
 *
 * קפיץ מוגדר בשני מספרים של מעצב, לא בפיזיקה: **יחס ריסון** `zeta` (1 — בלי קפיצה;
 * פחות — קפיצה קלה, רק אחרי מחווה שנשאה תנופה) ו**תגובה** `T` בשניות (כמה מהר מגיעים
 * ליעד; אין ״משך״ — הקפיץ נרגע כשנרגע). מה שהופך את הקפיץ לנכון למחוות:
 *
 * - **ניתן להפרעה:** יעד חדש באמצע תנועה רק מנתב מחדש — המהירות הנוכחית נשמרת, אין ״קיר״.
 * - **מסירת מהירות:** `v0` מהמחווה — התנועה ממשיכה במהירות האצבע, בלי תפר.
 * - **הקרנת תנופה** (`project`): לאן המחווה הולכת, לא איפה שחררו (פונקציית הפיזור של iOS).
 * - **גבול רך** (`rubber`): התנגדות שגדלה ככל שמושכים מעבר לקצה.
 *
 * אינטגרציה חצי־מרומזת ב־4 תת־צעדים לכל מסגרת, `dt` חסום ל־34ms (לשונית שחזרה מהרקע
 * לא ״מתפוצצת״). עם `prefers-reduced-motion` הקפיץ קופץ ליעד מיד (A-190).
 */

export interface Spring {
  /** הערך הנוכחי — מה שרואים על המסך. */
  x: number;
  /** המהירות (יחידות לשנייה). */
  v: number;
  /** היעד. */
  target: number;
  /** קשיחות וריסון — נגזרים מ־`zeta` ו־`T`. */
  k: number;
  c: number;
  /** מזהה ה־requestAnimationFrame; 0 — נח. */
  raf: number;
  onUpdate: (x: number) => void;
  done: (() => void) | undefined;
}

export interface SpringOptions {
  /** יחס ריסון: 1 — בלי קפיצה; 0.8–0.85 — קפיצה קלה אחרי flick. */
  zeta?: number;
  /** תגובה בשניות. */
  T?: number;
  /** מהירות התחלתית — מסירת המהירות מהמחווה. */
  v0?: number;
  /** נקרא פעם אחת כשהקפיץ נרגע ביעד. */
  done?: () => void;
}

export function createSpring(x: number, onUpdate: (x: number) => void): Spring {
  return { x, v: 0, target: x, k: 0, c: 0, raf: 0, onUpdate, done: undefined };
}

/** האם המשתמש ביקש תנועה מופחתת — הקפיצים קופצים ליעד. */
export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** מספר תת־הצעדים במסגרת; חסם ה־dt (שניות). */
const SUBSTEPS = 4;
const MAX_DT = 0.034;
/** סף המנוחה: קרוב ליעד ואיטי — עוצרים ונצמדים. */
const REST_X = 0.2;
const REST_V = 3;

/**
 * מניע את הקפיץ ליעד. קפיץ שכבר בתנועה — רק מנתב מחדש (היעד, הפרמטרים ו־`done`
 * מתעדכנים; המהירות נשמרת אלא אם נמסרה `v0`).
 */
export function springTo(s: Spring, target: number, options: SpringOptions = {}): void {
  const zeta = options.zeta ?? 1;
  const T = options.T ?? 0.4;
  const w = (2 * Math.PI) / T;
  s.k = w * w;
  s.c = 2 * zeta * w;
  s.target = target;
  s.done = options.done;
  if (options.v0 !== undefined) s.v = options.v0;
  if (prefersReducedMotion()) {
    stopSpring(s);
    settle(s);
    return;
  }
  if (s.raf !== 0) return;
  let last = performance.now();
  const step = (now: number): void => {
    const dt = Math.min((now - last) / 1000, MAX_DT);
    last = now;
    const h = dt / SUBSTEPS;
    for (let i = 0; i < SUBSTEPS; i++) {
      const a = -s.k * (s.x - s.target) - s.c * s.v;
      s.v += a * h;
      s.x += s.v * h;
    }
    if (Math.abs(s.x - s.target) < REST_X && Math.abs(s.v) < REST_V) {
      s.raf = 0;
      settle(s);
      return;
    }
    s.onUpdate(s.x);
    s.raf = requestAnimationFrame(step);
  };
  s.raf = requestAnimationFrame(step);
}

/** נצמד ליעד ומודיע — פעם אחת. */
function settle(s: Spring): void {
  s.x = s.target;
  s.v = 0;
  const done = s.done;
  s.done = undefined;
  s.onUpdate(s.x);
  done?.();
}

/** עוצר את הקפיץ במקומו (לפני גרירה: האצבע משתלטת). המהירות נשארת לטובת הגרירה. */
export function stopSpring(s: Spring): void {
  if (s.raf !== 0) cancelAnimationFrame(s.raf);
  s.raf = 0;
}

/**
 * הקרנת תנופה: כמה עוד יזוז משהו שמשוחרר במהירות `v` (פיקסלים לשנייה) עד שייעצר,
 * בפיזור מעריכי כמו גלילה. `d` ≈ 0.998 — תחושת גלילה רגילה; 0.99 — קצר יותר.
 */
export function project(v: number, d = 0.998): number {
  return ((v / 1000) * d) / (1 - d);
}

/**
 * גבול רך (rubber band): כמה האלמנט עוקב אחרי האצבע כש־`over` הוא המרחק מעבר לקצה
 * ו־`dim` ממד המשטח. ככל שמושכים יותר — פחות עוקב; תמיד קטן מ־`over`, ולעולם לא מגיע ל־`dim`.
 */
export function rubber(over: number, dim: number, c = 0.55): number {
  return (over * dim * c) / (dim + c * Math.abs(over));
}
